/**
 * FormField — RTL-aware label + input wrapper.
 * Provides: label above, error border state, helper text below.
 * Direction comes from ThemedInput reading document.documentElement.dir — no cloneElement for dir.
 * @layer-1 — mandatory wrapper for all form inputs.
 */
import { getEffectiveDir } from "@/shared/lib/direction";
import { cn } from "@/shared/lib/utils";
import { forwardRef } from "react";
import React from "react";
import { RequiredAsterisk } from "./RequiredAsterisk";

interface FormFieldProps {
  /** Label rendered above the input — accepts plain text or JSX */
  label?: React.ReactNode;
  /** Appends a red asterisk after the label with RTL-safe spacing */
  required?: boolean;
  /** Error border state — red border on the wrapped input */
  error?: boolean;
  /** Helper or error text rendered below the input */
  helperText?: string;
  /** Additional className for the outer wrapper */
  className?: string;
  /** Additional className for the label */
  labelClassName?: string;
  children: React.ReactNode;
}

const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      required = false,
      error = false,
      helperText,
      className,
      labelClassName,
      children,
    },
    ref
  ) => {
    return (
      <div ref={ref} className={cn("space-y-1.5", className)}>
        {label && (
          <div
            className={cn(
              "text-[11px] font-bold uppercase tracking-[0.16em]",
              "text-start",
              labelClassName
            )}
            style={{ color: "var(--color-muted-text)" }}
          >
            {label}
            {required && <RequiredAsterisk />}
          </div>
        )}
        {React.Children.map(children, (child) => {
          if (!React.isValidElement<{ error?: boolean }>(child)) return child;
          if (child.props.error !== undefined) return child;
          return React.cloneElement(child, { error });
        })}
        {helperText && (
          <div
            className={cn("text-xs", error ? "text-[var(--destructive)]" : "text-[var(--text-tertiary)]")}
            dir={getEffectiveDir()}
          >
            {helperText}
          </div>
        )}
      </div>
    );
  }
);
FormField.displayName = "FormField";

export { FormField };